import { type ReactNode } from "react";

import { useShellLayout } from "../context";
import { cx } from "../cx";
import { useAnimTick } from "../hooks/useAnimTick";

interface RootProps {
  "aria-label": string;
  children?: ReactNode;
}

export const Root = ({
  children,
  "aria-label": ariaLabel,
}: Readonly<RootProps>) => {
  const { isLeftSidebarVisible: isVisible, leftSidebarWidth: storedWidth } =
    useShellLayout();
  const computedWidth = isVisible ? storedWidth : 0;

  const { isAnimating, endAnimation } = useAnimTick(isVisible);

  return (
    <div
      style={{ width: computedWidth, minWidth: computedWidth }}
      className={cx(
        "isolate shrink-0 overflow-hidden",
        isAnimating &&
          "transition-[width,min-width] duration-[240ms] ease-[cubic-bezier(0.32,0.72,0,1)]",
      )}
      onTransitionEnd={e => {
        if (e.currentTarget === e.target && e.propertyName === "width")
          endAnimation();
      }}
    >
      <aside
        data-testid="circle-ai-shell-sidebar-root"
        aria-label={ariaLabel}
        aria-hidden={!isVisible || undefined}
        className="flex size-full min-h-0 flex-col overflow-hidden border-r border-primary dark:border-secondary"
      >
        {isVisible || isAnimating ? children : null}
      </aside>
    </div>
  );
};

Root.displayName = "CircleAIAppShell.Layout.Sidebar";
